/**
 * useDebouncedValue Hook
 *
 * Delays the update of a value by a specified amount of time.
 * Useful for search inputs to avoid making API calls on every keystroke.
 */

import { useEffect, useState } from "react";

/**
 * Returns a debounced version of the input value
 * @param value - The value to debounce
 * @param delay - The delay in milliseconds (default: 500ms)
 * @returns The debounced value
 */
export function useDebouncedValue<T>(value: T, delay = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup function to cancel the timeout if value changes
    // or component unmounts before delay period
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}
